/**
 * fastFetch — iter 150 Performance Layer
 *
 * Stale-while-revalidate cache wrapper around axios. Solves the "every card
 * blanks for 5-7s every visit" problem by:
 *   1. localStorage cache (persists across reloads / tab-switches)
 *   2. In-flight de-duplication (3 cards hitting /api/preload = 1 network call)
 *   3. SWR — instantly return cached value, refresh in background, push fresh
 *      value via callback
 *   4. Configurable TTLs per endpoint family
 *
 * Usage:
 *   import { fastGet, fastPost, clearCache } from "../lib/fastFetch";
 *
 *   useEffect(() => {
 *     const stop = fastGet(`${API}/omega/glossary`, setData);
 *     return stop;  // cancels any in-flight refresh on unmount
 *   }, []);
 */
import axios from "axios";

const CACHE_VERSION = "v2";
const STORAGE_PREFIX = `ff_${CACHE_VERSION}__`;
const MAX_CACHE_ITEM_BYTES = 256 * 1024;  // 256 KB — anything larger is bypassed

// Default TTLs in milliseconds, matched by URL substring.
// Order matters — first match wins.
const TTL_RULES = [
  // Reference / doctrine — barely changes
  { match: "/omega/glossary",         ttl: 24 * 60 * 60 * 1000 },  // 24h
  { match: "/omega/known-limits",     ttl: 24 * 60 * 60 * 1000 },
  { match: "/omega/sector-intel",     ttl: 6 * 60 * 60 * 1000 },
  { match: "/omega/growth-arc",       ttl: 5 * 60 * 1000 },
  // Frequently-refreshed signals
  { match: "/omega/calibration",      ttl: 5 * 60 * 1000 },
  { match: "/omega/sensing/overview", ttl: 60 * 1000 },
  { match: "/omega/sensing/triaxis",  ttl: 5 * 60 * 1000 },
  { match: "/omega/event-push/recent",ttl: 60 * 1000 },
  // Market / autopilot
  { match: "/preload",                ttl: 30 * 1000 },
  { match: "/autopilot/performance",  ttl: 60 * 1000 },
  { match: "/autopilot/positions",    ttl: 15 * 1000 },
  { match: "/daily-pnl",              ttl: 15 * 1000 },
  // Five components polled this with raw axios at 5-10s each; one shared
  // cached read now serves them all within the TTL.
  { match: "/omega/watch",            ttl: 5 * 1000 },
  { match: "/omega/market-status",    ttl: 30 * 1000 },
  { match: "/autopilot/decisions",    ttl: 60 * 1000 },
  { match: "/autopilot/scans/recent", ttl: 60 * 1000 },
  { match: "/autopilot/outcomes",     ttl: 5 * 60 * 1000 },
  { match: "/market/news",            ttl: 90 * 1000 },
  { match: "/market/quotes",          ttl: 10 * 1000 },
  // Fallback
  { match: "*",                       ttl: 60 * 1000 },
];

function _ttlForUrl(url) {
  for (const rule of TTL_RULES) {
    if (rule.match === "*" || url.includes(rule.match)) return rule.ttl;
  }
  return 60_000;
}

// ─── localStorage helpers (silent failure on quota / disabled storage) ─────
function _key(url) { return STORAGE_PREFIX + url; }

function _readCache(url) {
  try {
    const raw = localStorage.getItem(_key(url));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;  // { v: data, t: timestamp, e: errored? }
  } catch (_) {
    return null;
  }
}

function _writeCache(url, value) {
  try {
    const serialized = JSON.stringify({ v: value, t: Date.now() });
    if (serialized.length > MAX_CACHE_ITEM_BYTES) return;  // skip giant payloads
    localStorage.setItem(_key(url), serialized);
  } catch (_) {
    // QuotaExceeded — best effort prune oldest 10 entries
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
      const aged = keys.map(k => {
        try { return { k, t: JSON.parse(localStorage.getItem(k)).t || 0 }; }
        catch (_) { return { k, t: 0 }; }
      }).sort((a, b) => a.t - b.t).slice(0, 10);
      aged.forEach(({ k }) => localStorage.removeItem(k));
    } catch (_) {}
  }
}

export function clearCache(urlSubstring) {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
    keys.forEach(k => {
      if (!urlSubstring || k.includes(urlSubstring)) localStorage.removeItem(k);
    });
  } catch (_) {}
}

// ─── In-flight de-duplication ──────────────────────────────────────────────
const _inflight = new Map();  // url → Promise

function _dedupedGet(url, axiosOpts) {
  if (_inflight.has(url)) return _inflight.get(url);
  const p = axios.get(url, axiosOpts)
    .then(r => { _inflight.delete(url); return r; })
    .catch(e => { _inflight.delete(url); throw e; });
  _inflight.set(url, p);
  return p;
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * SWR-style GET. Calls onData up to TWICE:
 *   1) instantly with cached value (if present and fresh-ish)
 *   2) with fresh network value once it arrives
 *
 * Returns an unsubscribe function that prevents the late callback from firing
 * after a component unmounts.
 */
export function fastGet(url, onData, onError, opts = {}) {
  let alive = true;
  const { force = false, axiosOpts = {}, ttl = _ttlForUrl(url) } = opts;

  const cached = _readCache(url);
  if (cached && cached.v !== undefined && !force) {
    try { onData(cached.v, { fromCache: true, age: Date.now() - cached.t }); } catch (_) {}
    // If still fresh and not forced, we still re-validate silently for
    // freshness — but ONLY if older than 50% of TTL (avoid hammering).
    if (Date.now() - cached.t < ttl * 0.5) {
      return () => { alive = false; };
    }
  }

  _dedupedGet(url, axiosOpts)
    .then(r => {
      if (!alive) return;
      _writeCache(url, r.data);
      try { onData(r.data, { fromCache: false }); } catch (_) {}
    })
    .catch(e => {
      if (!alive) return;
      if (onError) {
        try { onError(e); } catch (_) {}
      }
    });

  return () => { alive = false; };
}

/**
 * Plain promise GET that still benefits from cache + dedup but is awaitable.
 * Returns { data, fromCache } so callers can decide whether to skeleton-flash.
 */
export async function fastGetAsync(url, opts = {}) {
  const { force = false, axiosOpts = {}, ttl = _ttlForUrl(url) } = opts;
  const cached = _readCache(url);
  if (cached && cached.v !== undefined && !force && (Date.now() - cached.t < ttl)) {
    // Trigger a background refresh if half-stale; but return cached now.
    if (Date.now() - cached.t > ttl * 0.5) {
      _dedupedGet(url, axiosOpts).then(r => _writeCache(url, r.data)).catch(() => {});
    }
    return { data: cached.v, fromCache: true, age: Date.now() - cached.t };
  }
  const r = await _dedupedGet(url, axiosOpts);
  _writeCache(url, r.data);
  return { data: r.data, fromCache: false };
}

/**
 * Always-network POST. Just adds in-flight dedup so duplicate concurrent
 * submits collapse. No cache.
 */
export async function fastPost(url, body, axiosOpts = {}) {
  return axios.post(url, body, axiosOpts);
}

// ─── Optional: pre-warm key endpoints in the background ────────────────────
// Call on app boot after auth so first-tap to any tab is instant.
export function prewarm(urls = []) {
  urls.forEach(u => {
    const cached = _readCache(u);
    if (!cached || Date.now() - cached.t > _ttlForUrl(u)) {
      _dedupedGet(u).then(r => _writeCache(u, r.data)).catch(() => {});
    }
  });
}
